import { useState } from "react";
import { motion, useIsPresent } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { imageSrc } from "../lib/api";
import { LivingPainting } from "./living/LivingPainting";
import { EnvelopeMemories } from "./notes/EnvelopeMemories";
import { ShareControl } from "./share/ShareSheet";
import { RightsLine } from "./Rights";
import { HeartToggle } from "./HeartBloom";
import { FavouriteMark } from "./FavouriteMark";

// a quoted main line keeps its attribution wherever it appears
const LINE_CITES = { destiny: "Emily Brontë, Wuthering Heights" };
const stepBtn = "font-display text-base italic text-[#5B3A85] underline-offset-4 transition-colors duration-200 hover:text-[#2D1E3E] hover:underline";

// one painting at a time: back in the room, on to the next (or the next feeling after the last)
function DetailsSteps({ nav, onStep }) {
  return (
    <div data-testid="details-steps" className="-mt-2 flex items-center gap-6">
      {nav.prev && (
        <button data-testid="details-prev-button" onClick={() => onStep(nav.prev)} className={stepBtn}>
          ‹ Previous
        </button>
      )}
      {nav.next ? (
        <button data-testid="details-next-button" onClick={() => onStep(nav.next)} className={stepBtn}>
          Next painting ›
        </button>
      ) : (
        <button data-testid="details-next-feeling-button" onClick={() => onStep(null, nav.nextRoom)} className={stepBtn}>
          next feeling · {nav.nextName} ›
        </button>
      )}
    </div>
  );
}

export default function DetailsView({ painting, onBack, backLabel = "Back to gallery", nav, onStep }) {
  const src = imageSrc(painting.image_url);
  const present = useIsPresent();
  const [memory, setMemory] = useState(null);
  return (
    <motion.div
      data-testid="details-view-container"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9 }}
      className={`fixed inset-0 z-[55] overflow-y-auto bg-gradient-to-br from-[#E9D5FF] via-[#FBCFE8] to-[#BAE6FD] ${present ? "" : "pointer-events-none"}`}
    >
      <img src={src} alt="" className="pointer-events-none fixed inset-0 h-full w-full scale-110 object-cover opacity-[0.3] blur-3xl" />
      <div className="relative flex min-h-full flex-col items-center justify-center gap-10 px-6 pb-16 pt-28 md:flex-row md:py-16 md:gap-20 md:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="border-[10px] border-white shadow-[0_30px_80px_rgba(91,58,133,0.22)]"
        >
          <div className="relative">
            <LivingPainting painting={painting} testid="details-painting-image" className="block h-auto" style={{ width: `min(86vw, 860px, ${72 * (painting.aspect_ratio || 1)}vh)` }} />
            <span data-testid="details-signature" aria-hidden className="pointer-events-none absolute bottom-[3%] right-[4%] select-none font-script text-[clamp(16px,2.6vw,28px)] text-white/75 [text-shadow:0_1px_3px_rgba(30,15,40,0.7),0_0_10px_rgba(30,15,40,0.35)]">
              Priyanka Jha
            </span>
          </div>
          {/* her main line, centred below the painting, never across it */}
          {painting.note?.trim() && (
            <p data-testid="details-note" className="mx-auto max-w-[min(86vw,860px)] bg-white px-4 pb-1 pt-4 text-center font-display text-xl italic leading-relaxed text-[#4A3660]">
              {painting.note}
              {LINE_CITES[painting.slug] && <span data-testid="details-note-cite" className="mt-1 block font-display text-sm not-italic text-[#7C6992]">{LINE_CITES[painting.slug]}</span>}
            </p>
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.1, delay: 0.35, ease: "easeOut" }}
          className="flex max-w-md flex-col gap-6"
        >
          <div className="flex flex-col gap-2">
            <span data-testid="details-feeling" className="self-start rounded-full border border-[#7E57C2]/30 bg-[#7E57C2]/10 px-4 py-1 font-display text-lg italic text-[#5B3A85]">
              {painting.feeling_word || painting.emotion}
            </span>
            {(painting.description || painting.emotion) && (painting.description || painting.emotion) !== painting.feeling_word && (
              <span data-testid="details-emotion" className="text-[10px] uppercase tracking-[0.35em] text-[#7E57C2]/70">
                {painting.description || painting.emotion}
              </span>
            )}
          </div>
          <h1 data-testid="details-title" className="font-display text-5xl font-light leading-[1.05] text-[#2D1E3E] lg:text-6xl">{painting.title}</h1>
          <FavouriteMark painting={painting} testid="details-favourite" className="-mt-3" />
          {painting.about?.trim() && (
            <p data-testid="details-about" className="-mt-2 whitespace-pre-line pl-5 font-display text-base leading-relaxed text-[#6E5C82]">
              {painting.about}
            </p>
          )}
          {painting.style?.trim() && (
            <p data-testid="details-style" className="-mt-2 pl-5 font-display text-sm italic leading-relaxed text-[#7C6992]">
              {painting.style}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <a href="/about" data-testid="details-artist-link" className="font-display text-base italic text-[#6E5C82] underline-offset-4 transition-colors duration-200 hover:text-[#5B3A85] hover:underline">
              Priyanka Jha
            </a>
            <ShareControl painting={painting} memory={memory} />
            <HeartToggle painting={painting} testid="heart-toggle-details" />
          </div>
          <button
            data-testid="back-to-gallery-button"
            onClick={onBack}
            className="group mt-4 flex items-center gap-3 self-start rounded-full border border-[#7E57C2]/40 bg-white/80 px-7 py-3 text-xs uppercase tracking-[0.25em] text-[#2D1E3E] transition-colors duration-300 hover:bg-[#7E57C2] hover:text-white"
          >
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            {backLabel}
          </button>
          {nav && onStep && <DetailsSteps nav={nav} onStep={onStep} />}
          <EnvelopeMemories painting={painting} onSent={setMemory} />
          <p data-testid="details-medium" className="mt-2 font-display text-sm italic text-[#7C6992]/80">Acrylic on canvas</p>
        </motion.div>
      </div>
      <RightsLine testid="rights-line-details" className="relative mx-auto block pb-6 text-center" />
    </motion.div>
  );
}
